import {
  BreastProjectionXRayImageStorageForPresentation,
  BreastProjectionXRayImageStorageForProcessing,
  BreastTomosynthesisImageStorage,
  DigitalMammographyXRayImageStorageForPresentation,
  DigitalMammographyXRayImageStorageForProcessing,
} from 'domains/viewer/Viewer/SOPClasses';
import type { Frame, Stack } from 'domains/viewer/ViewportsConfigurations/types';
import type { BaseImagingProvider } from './BaseImagingProvider';
import { findMatchingSeriesWithinStudy } from './classificationBasedMatching';

export const HybridMammographyView = {
  DBT: 'DBT',
  MG: 'MG',
} as const;

export type HybridMammographyView =
  (typeof HybridMammographyView)[keyof typeof HybridMammographyView];

export interface HybridMammographySliceInfo {
  /** One-based position within frames of the current view, intended for display. */
  position: number;
  total: number;
  view: HybridMammographyView;
}

const MAMMOGRAPHY_2D_SOP_CLASSES = new Set([
  BreastProjectionXRayImageStorageForPresentation,
  BreastProjectionXRayImageStorageForProcessing,
  DigitalMammographyXRayImageStorageForPresentation,
  DigitalMammographyXRayImageStorageForProcessing,
]);

/** Classifies a frame as a 2D mammogram, tomosynthesis image, or neither from its SOP class. */
export const getHybridMammographyView = (
  frame: Pick<Frame, 'sopClassUID'>
): HybridMammographyView | null => {
  if (frame.sopClassUID === BreastTomosynthesisImageStorage) {
    return HybridMammographyView.DBT;
  }

  if (MAMMOGRAPHY_2D_SOP_CLASSES.has(frame.sopClassUID)) {
    return HybridMammographyView.MG;
  }

  return null;
};

/** Returns frames only for non-empty worker-loaded single-stack providers. */
const getSingleStackFrames = (
  provider: BaseImagingProvider<Stack>
): ReadonlyArray<Frame> | null => {
  if (provider.providerType !== 'worker-loaded' || !('frames' in provider.stack)) {
    return null;
  }

  return provider.stack.frames.length > 0 ? provider.stack.frames : null;
};

/**
 * Finds same-study, classification-matched providers that collectively offer both MG and DBT.
 * The returned order follows `stackProviders`, which defines the view-switch cycle.
 */
export const getHybridMammographySwapProviders = (
  provider: Nullable<BaseImagingProvider<Stack>>,
  stackProviders: ReadonlyMap<string, BaseImagingProvider<Stack>>
): ReadonlyArray<BaseImagingProvider<Stack>> => {
  if (provider == null) return [];

  const frames = getSingleStackFrames(provider);
  const currentFrame = frames?.[0];
  const currentView = currentFrame == null ? null : getHybridMammographyView(currentFrame);
  if (currentFrame == null || currentView == null) return [];

  const matchingSeriesSmids = new Set(
    findMatchingSeriesWithinStudy(currentFrame.series.smid, provider.study)
  );
  matchingSeriesSmids.add(currentFrame.series.smid);

  const orderedProviders: Array<BaseImagingProvider<Stack>> = [];
  const availableViews = new Set<HybridMammographyView>();
  for (const candidate of stackProviders.values()) {
    if (candidate.study.smid !== provider.study.smid) {
      continue;
    }

    const candidateFrames = getSingleStackFrames(candidate);
    const candidateFrame = candidateFrames?.[0];
    if (candidateFrame == null || !matchingSeriesSmids.has(candidateFrame.series.smid)) {
      continue;
    }

    const candidateView = getHybridMammographyView(candidateFrame);
    if (candidateView == null) continue;

    orderedProviders.push(candidate);
    availableViews.add(candidateView);
  }

  if (
    !availableViews.has(HybridMammographyView.MG) ||
    !availableViews.has(HybridMammographyView.DBT)
  ) {
    return [];
  }

  return orderedProviders;
};

/** Returns the next eligible provider in the hybrid MG/DBT cycle, wrapping at the end. */
export const findHybridMammographySwapTarget = (
  provider: Nullable<BaseImagingProvider<Stack>>,
  stackProviders: ReadonlyMap<string, BaseImagingProvider<Stack>>
): BaseImagingProvider<Stack> | null => {
  if (provider == null) return null;

  const orderedProviders = getHybridMammographySwapProviders(provider, stackProviders);

  const currentIndex = orderedProviders.findIndex(
    (candidate) => candidate.stack.smid === provider.stack.smid
  );
  if (currentIndex === -1 || orderedProviders.length < 2) return null;

  return orderedProviders[(currentIndex + 1) % orderedProviders.length] ?? null;
};

/**
 * Describes the active frame within its MG or DBT subset. `position` is converted from the
 * zero-based array index to the one-based value displayed in the viewport overlay.
 */
export const getHybridMammographySliceInfo = (
  provider: BaseImagingProvider<Stack>,
  activeSlice: number
): HybridMammographySliceInfo | null => {
  const frames = getSingleStackFrames(provider);
  const activeFrame = frames?.[activeSlice];
  if (frames == null || activeFrame == null) return null;

  const view = getHybridMammographyView(activeFrame);
  if (view == null) return null;

  const matchingFrames = frames.filter((frame) => getHybridMammographyView(frame) === view);
  const position = matchingFrames.indexOf(activeFrame);
  if (position === -1) return null;

  return {
    position: position + 1,
    total: matchingFrames.length,
    view,
  };
};
